async function correctWordTimings(words, audioBuffer, onProgress = () => {}) {
    // Enable/disable specific features
    const ENABLE_BIQUAD_FILTER = true; // Enables bandpass filtering
    const ENABLE_SPECTRAL_FLUX = true; // Enables spectral flux analysis
    const ENABLE_ZCR = false; // Enables Zero-Crossing Rate (ZCR) analysis
    const ENABLE_VAD = false; // Enables Voice Activity Detection (VAD)

    // FFT size for frequency analysis; smaller values increase temporal resolution but reduce frequency resolution
    const FFT_SIZE = 512;

    // Threshold for spectral flux; lower values make the algorithm more sensitive to changes in energy
    const THRESHOLD_FLUX = 0.01;

    // Energy threshold for Voice Activity Detection (VAD); lower values make the algorithm more sensitive to quieter sounds
    const ENERGY_THRESHOLD = 0.01;

    // Zero-Crossing Rate (ZCR) threshold; higher values allow noisier signals to be considered speech
    const ZCR_THRESHOLD = 0.15;

    // Time offset (in seconds) for extending the analysis range before and after the word boundaries
    const OFFSET_SECONDS = 0.05;

    // Bandpass filter frequency range; adjust to focus on human speech frequencies
    const BANDPASS_LOW_FREQ = 100; // Lower frequency bound (Hz)
    const BANDPASS_HIGH_FREQ = 5000; // Upper frequency bound (Hz)

    // Create an offline audio context for precise analysis
    const offlineContext = new OfflineAudioContext(
        audioBuffer.numberOfChannels,
        audioBuffer.length,
        audioBuffer.sampleRate
    );

    // Copy the audio buffer into the offline context
    const source = offlineContext.createBufferSource();
    source.buffer = audioBuffer;

    // Create a bandpass filter to focus on the human speech frequency range
    let bandpassFilter;
    if (ENABLE_BIQUAD_FILTER) {
        bandpassFilter = offlineContext.createBiquadFilter();
        bandpassFilter.type = 'bandpass';
        bandpassFilter.frequency.value = (BANDPASS_LOW_FREQ + BANDPASS_HIGH_FREQ) / 2;
        bandpassFilter.Q.value = 0.7; // Default Q for moderate bandwidth
        source.connect(bandpassFilter);
    }

    // Create an analyser for signal analysis
    const analyser = offlineContext.createAnalyser();
    analyser.fftSize = FFT_SIZE;

    const dataArray = new Float32Array(analyser.frequencyBinCount);

    // Connect the nodes based on the selected options
    if (ENABLE_BIQUAD_FILTER) {
        bandpassFilter.connect(analyser);
    } else {
        source.connect(analyser);
    }
    analyser.connect(offlineContext.destination);

    // Start playback in the offline context
    source.start();

    // Render the audio
    const renderedBuffer = await offlineContext.startRendering();

    // Get the audio data from the rendered buffer
    const channelData = renderedBuffer.getChannelData(0);

    // Apply Hann windowing to smooth transitions
    function applyHannWindow(samples) {
        const windowedSamples = new Float32Array(samples.length);
        for (let i = 0; i < samples.length; i++) {
            const multiplier = 0.5 * (1 - Math.cos((2 * Math.PI * i) / (samples.length - 1)));
            windowedSamples[i] = samples[i] * multiplier;
        }
        return windowedSamples;
    }

    // Calculate spectral flux between two frames
    function calculateSpectralFlux(prevSpectrum, currSpectrum) {
        if (!prevSpectrum || !currSpectrum || prevSpectrum.length !== currSpectrum.length) {
            return 0;
        }

        let flux = 0;
        for (let i = 0; i < currSpectrum.length; i++) {
            const diff = currSpectrum[i] - prevSpectrum[i];
            flux += diff > 0 ? diff : 0; // Only count increases
        }
        return flux;
    }

    // Calculate zero-crossing rate for a segment
    function calculateZeroCrossingRate(samples) {
        let zeroCrossings = 0;
        for (let i = 1; i < samples.length; i++) {
            if (samples[i - 1] * samples[i] < 0) {
                zeroCrossings++;
            }
        }
        return zeroCrossings / samples.length;
    }

    // Simple Voice Activity Detection (VAD) based on energy threshold
    function detectSpeechActivity(samples) {
        const energy = samples.reduce((sum, sample) => sum + sample ** 2, 0) / samples.length;
        return energy > ENERGY_THRESHOLD;
    }

    let previousWord = null; // Track the previous word to prevent overlaps
    const correctedWords = [];
    const batchSize = 4;

    for (let i = 0; i < words.length; i += batchSize) {
        const batch = words.slice(i, i + batchSize);
        
        for (const word of batch) {
            const { time, duration } = word;

            // Convert word timings to sample indices
            const startSample = Math.floor(time * renderedBuffer.sampleRate);
            const endSample = Math.min(
                Math.floor((time + duration) * renderedBuffer.sampleRate),
                channelData.length - 1
            );

            // Define offset for extended analysis (in samples)
            const offset = Math.floor(OFFSET_SECONDS * renderedBuffer.sampleRate);
            const extendedStartSample = Math.max(0, startSample - offset);
            const extendedEndSample = Math.min(channelData.length - 1, endSample + offset);

            // Extract extended segment
            const extendedSegment = channelData.slice(extendedStartSample, extendedEndSample);
            const windowedSegment = applyHannWindow(extendedSegment);

            // Analyze within the extended range
            let correctedStart = extendedStartSample;
            let correctedEnd = extendedEndSample;
            let maxFlux = -Infinity;
            let prevSpectrum = null;

            if (ENABLE_SPECTRAL_FLUX) {
                for (let i = 0; i < windowedSegment.length - analyser.fftSize / 2; i++) {
                    // Perform FFT analysis for spectral flux
                    analyser.getFloatFrequencyData(dataArray);
                    const currSpectrum = Array.from(dataArray); // Convert Float32Array to array

                    // Skip if it's the first iteration
                    if (!prevSpectrum) {
                        prevSpectrum = currSpectrum;
                        continue; // Skip calculation for the first spectrum
                    }

                    const flux = calculateSpectralFlux(prevSpectrum, currSpectrum);
                    prevSpectrum = currSpectrum; // Update prevSpectrum for the next iteration

                    if (flux > maxFlux && flux > THRESHOLD_FLUX) {
                        correctedStart = extendedStartSample + i;
                        maxFlux = flux;
                    }
                }

                prevSpectrum = null; // Reset for end analysis

                for (let i = windowedSegment.length - 1; i > analyser.fftSize / 2; i--) {
                    // Perform FFT analysis for spectral flux
                    analyser.getFloatFrequencyData(dataArray);
                    const currSpectrum = Array.from(dataArray);

                    // Skip if it's the first iteration
                    if (!prevSpectrum) {
                        prevSpectrum = currSpectrum;
                        continue; // Skip calculation for the first spectrum
                    }

                    const flux = calculateSpectralFlux(prevSpectrum, currSpectrum);
                    prevSpectrum = currSpectrum; // Update prevSpectrum for the next iteration

                    if (flux > maxFlux && flux > THRESHOLD_FLUX) {
                        correctedEnd = extendedStartSample + i;
                        maxFlux = flux;
                    }
                }
            }

            // Use Zero-Crossing Rate (ZCR) and VAD for refinement
            if (ENABLE_ZCR || ENABLE_VAD) {
                const zcr = ENABLE_ZCR ? calculateZeroCrossingRate(windowedSegment) : 0;
                const isSpeech = ENABLE_VAD ? detectSpeechActivity(windowedSegment) : true;

                if ((!isSpeech && ENABLE_VAD) || (zcr > ZCR_THRESHOLD && ENABLE_ZCR)) {
                    // Ignore regions with high ZCR or non-speech activity
                    return { ...word, time, duration };
                }
            }

            // Convert sample indices back to time values
            let correctedTime = correctedStart / renderedBuffer.sampleRate;
            let correctedDuration =
                (correctedEnd - correctedStart) / renderedBuffer.sampleRate;

            if (previousWord) {
                const previousEndTime = previousWord.time + previousWord.duration;
                
                if (correctedTime < previousEndTime) {
                    previousWord.duration = Math.max(0, correctedTime - previousWord.time);
                }
            }

            previousWord = {
                ...word,
                time: correctedTime,
                duration: correctedDuration,
            };

            correctedWords.push(previousWord);
        }

        //Release main UI thread
        const progress = Math.round((correctedWords.length / words.length) * 100);
        onProgress(progress);
        await new Promise((resolve) => setTimeout(resolve, 0));
    }

    // Final call to onProgress when processing is complete
    onProgress(100);

    return correctedWords;
}

export {
    correctWordTimings
}